/**
 * Tier-2 — the org admin's budget dashboard (`manage_budget:read`).
 *
 * Read-only by design: every write lives in the Budgets editor behind
 * `manage_budget:write`, so a read-only admin can't be handed a control
 * the server would reject.
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useMyCapabilities, useRefreshWhenShown } from '@papan/core-ui/ui'
import { ChartRenderer } from '@papan/charts/ui'
import type { BudgetCap } from '@papan/types'
import { budgetApi, type OrgUsageResponse } from '../../client/budget'
import { MonthPicker, periodKey } from './MonthPicker'
import { bucketApplies } from './logic'
import { billingApi } from '@papan/billing/client'
import {
  useMoney, FigureText, MoneyText, CapText, Band, StatTile,
  EnforcementNote, FeatureBreakdown, UsageMeter, useFeatureChart,
} from './bits'

/** Past this many members the table gains a filter box; below it, a filter is
 *  one more control on a screen that already has several. Same constant name
 *  and value as `ModelRestrictionsEditor`'s, so the two long tables on this
 *  surface reach for a filter at the same point. */
const MEMBER_FILTER_AT = 8
/** Rows shown initially, and the step for "Show more". */
const MEMBER_PAGE = 25

/**
 * How close this member is to being stopped (M8).
 *
 * Spend and cap sat as two adjacent numbers, so the question the table exists
 * to answer — who is about to be blocked — was arithmetic, per row, repeated.
 *
 * The three cap states each mean something different here and none of them is
 * a percentage: `null` is unlimited (no fraction to take), `0` is a hard block
 * (not "100% used" — nothing was used), and only a positive cap has headroom.
 * Colour tracks the same thresholds as `UsageMeter` so a member reading amber
 * here and amber there is reading one rule.
 */
function Headroom({ used, cap, money }: { used: number; cap: BudgetCap; money: ReturnType<typeof useMoney> }) {
  const { t } = useTranslation('core-ui')
  if (cap === null) return <span className="themed-text-muted text-xs">—</span>
  if (cap === 0) return <span className="text-red-400 text-xs">{t('settings.budget.blocked')}</span>
  const left = Math.max(0, cap - used)
  const pct = Math.min(100, Math.max(0, (used / cap) * 100))
  const tone = pct >= 100 ? 'text-red-400' : pct >= 75 ? 'text-amber-400' : 'themed-text'
  return (
    <span className={`text-xs tabular-nums ${tone}`} title={money.note}>
      {money.fmt(left)} <span className="themed-text-muted">({Math.round(100 - pct)}%)</span>
    </span>
  )
}

export function OrgBudgetDashboard({
  tenantId,
  userEmail,
  onNavigate,
  onOpenBilling,
  personal = false,
}: {
  tenantId?: string
  userEmail?: string
  /** Settings-pane navigation, threaded from the app shell. Optional so the
   *  component still renders anywhere the shell doesn't provide it. */
  /** Jump to the spending-limits editor. The destination route is owned by
   *  the caller (App.tsx / MobileShell), so this takes no page argument. */
  onNavigate?: () => void
  /** Jump to Settings → Billing, where credit is bought. Optional: a host
   *  that cannot offer it simply renders the balance without the link. */
  onOpenBilling?: () => void
  /**
   * Personal-drive mode — the same page, minus everything that presupposes an
   * organization.
   *
   * A personal drive has exactly one member and no teams, so the per-member
   * and per-team tables, their two charts, and the member-count tile are all
   * ceremony over a set of one. The tiles are relabelled from "organization
   * spend / ceiling" to the personal wording the member view already uses.
   *
   * What SURVIVES is the reason to render this page at all on a personal
   * drive: **Spend by feature**. `MemberUsageView` answers "which pool is
   * funding me" and has no per-feature breakdown, so without this a solo user
   * had nowhere to see WHAT they spent on. The `/usage` endpoint already
   * returns `featureBreakdown` and a personal drive's owner holds
   * `manage_budget` on their own tenant, so this needs no new endpoint.
   */
  personal?: boolean
}) {
  const { t } = useTranslation('core-ui')
  // `personal` disables the capability QUERY and makes `can()` short-circuit to
  // TRUE (`useMyCapabilities`: "personal drive → implicit owner"), which is
  // right for reads and wrong for the one write this page offers.
  //
  // So `canWrite` must override it: there is no Budget editor on a personal
  // drive, and rendering "Edit budget" would link to `/settings/limits`, which
  // the personal sidebar deliberately does not carry. `canRead` is belt-and-
  // braces — `can()` already returns true — kept only so the intent survives if
  // that short-circuit ever changes.
  const { can } = useMyCapabilities(tenantId, personal, userEmail)
  const canRead = personal || can('manage_budget', 'read')
  // Whether Settings → Billing exists for this reader — the clock note names it.
  const canSeeBilling = personal || can('manage_billing', 'read')
  const canWrite = !personal && can('manage_budget', 'write')
  const [data, setData] = useState<OrgUsageResponse | null>(null)
  /** Per-member table controls (S3). Same thresholds as the model grid. */
  /** Which month is on screen (M1). Defaults to the current one; the endpoint
   *  has always accepted a period and nothing ever sent one. */
  const [monthPeriod, setMonthPeriod] = useState(() => periodKey(new Date()))
  /**
   * The prepaid balance (M7).
   *
   * The admin's real question is "will we run out?", and answering it needed
   * the ceiling (here) plus the balance (on Billing, behind a different
   * capability) — two pages. Read from the SAME endpoint the Billing page
   * uses rather than adding a field to `/usage`: one source, so the two
   * screens cannot disagree about how much credit is left.
   *
   * Best-effort. A member who cannot read credits simply gets no card, which
   * is the correct outcome and not worth an error state on a spend page.
   */
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [memberQuery, setMemberQuery] = useState('')
  const [memberLimit, setMemberLimit] = useState(MEMBER_PAGE)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * Re-fetch when the deck comes back on screen.
   *
   * The desktop shell keeps a deck MOUNTED behind `display: none`, so the
   * effect below is otherwise the only fetch this page ever runs — it served
   * whatever was true the first time Settings was opened, with no error and no
   * way back but a full page reload. That was the reported symptom on
   * Settings → Spending (2026-08-13).
   *
   * A counter in the dependency list rather than a hoisted `load` callback:
   * the fetch is written inline with an `alive` guard against out-of-order
   * responses, and hoisting it would move that guard for no gain.
   */
  const [reload, setReload] = useState(0)
  useRefreshWhenShown(() => setReload(n => n + 1))

  useEffect(() => {
    if (!tenantId || !canRead) { setLoading(false); return }
    let alive = true
    setLoading(true)
    budgetApi.getOrgUsage(tenantId, monthPeriod)
      .then(d => { if (alive) { setData(d); setError(null) } })
      .catch((e: any) => { if (alive) setError(e?.message || 'Failed to load usage') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [tenantId, canRead, monthPeriod, reload])

  useEffect(() => {
    if (!tenantId || !canRead) return
    let alive = true
    billingApi.getCredits({ limit: 1 })
      .then(r => { if (alive) setCreditBalance(r.balanceBillingMicro ?? null) })
      .catch(() => { if (alive) setCreditBalance(null) })
    return () => { alive = false }
  }, [tenantId, canRead])

  const money = useMoney()

  // ── Charts plot the BILLING-native figure, unconverted ──────────────
  //
  // These multiplied by `data.currency.displayCurrencyRate`. That was correct
  // when `usage.*` was canonical USD; since P6 the API sends billing-currency
  // micro, so the multiplier is a SECOND conversion — on an MYR deployment at
  // 5.5 it plotted spend at 5.5x the charged amount.
  //
  // It is the worst shape of this bug to spot by eye: every series scales by
  // the same factor, so the chart's SHAPE stays right and only the axis lies,
  // beside a legend and tiles that are correct.
  const userChart = useMemo(() => {
    if (!data) return null
    const rows = [...data.users]
      .sort((a, b) => b.usage.total - a.usage.total)
      .slice(0, 12)
      .map(u => ({
        name: u.email.split('@')[0],
        promo: +((u.usage.promo ?? 0) / 1_000_000).toFixed(4),
        free: +(u.usage.free / 1_000_000).toFixed(4),
        prepaid: +((u.usage.prepaid ?? 0) / 1_000_000).toFixed(4),
      }))
    // Overage buckets (extra/grace) are deliberately NOT charted — the money
    // surface no longer shows them to customers (2026-08-19). The wire still
    // carries `usage.extra`/`usage.grace`; this view just does not read them.
    if (!rows.some(r => r.promo || r.free || r.prepaid)) return null
    // The promo and credits series only join the stack when some row was
    // actually funded that way — a permanent empty legend entry would ask
    // every org "what promo?" / "what credits?"
    const hasPromo = rows.some(r => r.promo)
    const hasPrepaid = rows.some(r => r.prepaid)
    return {
      chartType: 'bar' as const,
      data: rows,
      categoryKey: 'name',
      stacked: true,
      height: 240,
      series: [
        ...(hasPromo ? [{ name: t('settings.budget.bucket.promo'), dataKey: 'promo', color: '#8064A2' }] : []),
        { name: t('settings.budget.bucket.free'), dataKey: 'free', color: '#4F81BD' },
        // Stacked in waterfall order: promo → free → prepaid.
        ...(hasPrepaid ? [{ name: t('settings.budget.bucket.prepaid'), dataKey: 'prepaid', color: '#4BACC6' }] : []),
      ],
    }
  }, [data, t])

  const teamChart = useMemo(() => {
    if (!data) return null
    const rows = data.teams.filter(x => x.used > 0).map(x => ({
      name: x.name,
      spend: +(x.used / 1_000_000).toFixed(4),
    }))
    if (!rows.length) return null
    return {
      chartType: 'pie' as const,
      data: rows,
      categoryKey: 'name',
      height: 240,
      series: [{ name: t('settings.budget.col.spend'), dataKey: 'spend' }],
    }
  }, [data, t])

  // Fed to both the pie and the list from ONE array, so the two cannot
  // disagree about which features exist. No longer filtered: the withholding
  // existed for a metered-but-unpriced `storage` row, and 0171 made storage a
  // quota that writes no ledger row at all.
  const featureRows = useMemo(() => data?.featureBreakdown ?? [], [data])

  /**
   * Is the person READING this page on a platform support seat here?
   *
   * Their own row already carries the server's answer — the pair
   * `resolvePlatformPayer` enforces, resolved for the period on screen — so
   * this reads it rather than re-deriving anything. Matched case-insensitively
   * because `userEmail` comes from the session while the row's is the
   * membership's, and only the latter is guaranteed lowercased.
   */
  const viewerSeat = useMemo(() => {
    const mine = userEmail?.trim().toLowerCase()
    if (!mine) return false
    return (data?.users ?? []).some(u => u.email.trim().toLowerCase() === mine && u.platformSeat)
  }, [data, userEmail])

  /** Highest spend first — unchanged — then narrowed and paged. */
  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase()
    return [...(data?.users ?? [])]
      .sort((a, b) => b.usage.total - a.usage.total)
      .filter(u => !q || u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q))
  }, [data, memberQuery])
  const shownMembers = useMemo(() => filteredMembers.slice(0, memberLimit), [filteredMembers, memberLimit])
  // Shared with the member's own Spending view — see `useFeatureChart`.
  const featureChart = useFeatureChart(featureRows, t)

  if (!tenantId) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noTenant')}</div>
  if (!canRead) return <div className="p-4 themed-text-muted text-sm">{t('settings.budget.noPermissionRead')}</div>
  if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  if (error || !data) return <div className="p-4 text-sm text-red-400">{error}</div>

  // `storage` is GONE from the response entirely. The previous note here said
  // it stayed because "Settings → Storage and MemberUsageView both read it" —
  // both halves were false: the Storage tab uses `storageApi.getComposition`
  // and MemberUsageView never referenced it. Nothing read it, so nothing
  // should have been paying four aggregates to produce it.
  const { tenant, users, teams } = data
  const hasCeilingMeter = tenant.caps.freeCap != null && tenant.caps.freeCap > 0

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h2 className="themed-text text-lg font-medium mb-1">{t('settings.budget.dashboard.title')}</h2>
          <p className="themed-text-muted text-sm">
            {t(personal ? 'settings.budget.dashboard.descSelf' : 'settings.budget.dashboard.desc', { period: data.period })}
          </p>
          {/* A2's mirror, restored (G6). The Billing page names both clocks and
              points here; this half was lost when `OrgBudgetPage` was unwound
              into the Spending/Budget pair, leaving the reconciliation
              one-directional — so an admin arriving from Billing met a
              different total with nothing explaining it.

              Gated on being able to OPEN the page it names. A member without
              `manage_billing:read` has no Billing item in the sidebar at all,
              and pointing them at it would be a dead reference. */}
          {canSeeBilling && (
            <p className="themed-text-muted text-xs mt-1">{t('settings.budget.clockNote')}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
        {/* Which month. Bounded to months that can have data — an open date
            field answers "March 2019" with an empty page, which reads as
            breakage rather than as "nothing happened". */}
        <MonthPicker value={monthPeriod} onChange={setMonthPeriod} />
        {/* This page is read-only by design; every knob lives in the Budget
            editor. Saying so here spares the admin hunting the sidebar for
            which of two similarly-named entries can actually change things. */}
        {canWrite && onNavigate && (
          <button
            onClick={() => onNavigate()}
            className="text-xs themed-btn-secondary px-3 py-1.5 rounded-lg shrink-0 font-medium transition-colors"
          >
            {t('settings.budget.dashboard.editBudget')}
          </button>
        )}
        </div>
      </div>

      {/* S1 — the answer first.
          The page opened with four tiles, a bar, then five sections; nothing
          said "are we OK?" until the reader had parsed a tile pair and done
          the subtraction. This states it in one sentence, from the same
          `tenant.usage`/`tenant.caps` the meter below uses, so the two cannot
          disagree.

          Four states, because a ceiling has four meanings and collapsing them
          is how "unlimited" and "blocked" come to look alike: a real ceiling
          with headroom, a real ceiling reached, no ceiling at all (null), and
          a hard block (0). */}
      {/* ── THE VIEWER'S OWN WALLET, WHEN IT IS NOT THIS ORG'S ──────────
          The notice that closed this for members landed on `MemberUsageView`
          only — and a support seat is granted to someone who is normally an
          ADMIN here, so `manage_budget:read` routes them to THIS branch
          instead. Everything below is the customer's: the lede, the meter and
          the headroom bar all read full while the platform org is exhausted
          and their own calls have stopped. The per-member badge on their row
          is addressed to the customer ("Their AI usage … is billed to the
          platform, not to you"), not to the person reading it. */}
      {viewerSeat && (
        <div className="mb-4 rounded border border-sky-500/30 bg-sky-500/10 px-3 py-2">
          <p className="themed-text text-sm">
            {t('settings.budget.platformSeatSelf')}
          </p>
          <p className="themed-text-muted text-xs mt-1">
            {t('settings.budget.platformSeatSelfHint')}
          </p>
        </div>
      )}

      {/* Free allowance + how much of it is used, FIRST and prominent
          (2026-08-19). The plan's included allowance and its utilisation is the
          answer a customer opens this page for. Overage ceilings (extra/grace)
          are no longer shown at all — the wire still carries them; this view
          simply does not read them.

          The lede states the answer in one sentence; the two tiles and the
          meter carry the figures. Four ceiling states, because collapsing them
          is how "unlimited" (null) and "blocked" (0) come to look alike. */}
      <Band
        title={t(personal ? 'settings.budget.includedAllowance' : 'settings.budget.orgCeiling')}
        info={t(personal ? 'settings.budget.includedAllowanceHint' : 'settings.budget.orgCeilingHint')}
      >
        {/* Just the utilisation bar — used vs included allowance. The separate
            spend + ceiling tiles that used to sit here restated exactly this
            (2026-08-19), so they were dropped to keep the header lean. The lede
            sentence is the FALLBACK for the two states a bar cannot express:
            blocked (0) and unlimited (null) have no fill fraction. */}
        {hasCeilingMeter ? (
          <UsageMeter
            label={t('settings.budget.meterFree')}
            usedMicro={tenant.usage.free}
            capMicro={tenant.caps.freeCap as number}
            money={money}
          />
        ) : (
          <div className="text-sm themed-text">
            {tenant.caps.freeCap === 0
              ? <span className="text-red-400">{t('settings.budget.lede.blocked')}</span>
              : t('settings.budget.lede.none', { used: money.fmt(tenant.usage.total) })}
          </div>
        )}
      </Band>

      {/* No storage on this page at all — neither a bar nor a cost row.
          Settings → Storage owns utilization (by type, largest folders,
          reclaimable), and a second smaller copy of it here was the same
          figure twice.

          This note used to promise a `storage` row in Spend by feature,
          "which the daily storage meter now fills". That meter was removed in
          0171: storage is a QUOTA, enforced against
          `tenants.storage_quota_bytes`, and it writes no ledger row and has no
          price. So there is nothing for a money page to say about it, and
          `FEATURE_ORDER` no longer carries it. Putting it back would render a
          permanently-$0 line.

          Zero-cost rows in general still render — `FeatureBreakdown` keeps
          them and marks them "Metered, not charged", so an unpriced surface
          shows its real quantity instead of disappearing. Only the pie above
          filters on `cost > 0`, where an unpriced feature would be an
          invisible slice. That behaviour is for compute and search; storage is
          not metered at all. */}

      {/* Where the money CAME FROM, beside where it went.
          On a personal drive this replaces what Settings → Usage used to
          carry: that item is gone (one subject, two doors), and the buckets
          were the half of it this page did not have. On an ORG it is new —
          the dashboard could show a stacked per-member chart but had no
          headline answer to "how much of this was included, and how much are
          we being billed for". Both read the same `tenant.usage`. */}
      {/* What is left to spend once the ceiling is reached — the other half of
          "will we run out?", which used to live on a different page behind a
          different capability. Read-only here; buying is Billing's job. */}
      {creditBalance !== null && creditBalance > 0 && (
        <Band title={t('settings.budget.credit.title')} info={t('settings.budget.credit.desc')} card>
          <div className="flex items-center justify-between gap-3">
            <MoneyText micro={creditBalance} money={money} className="themed-text text-lg font-medium" />
            {canSeeBilling && onOpenBilling && (
              <button onClick={onOpenBilling} className="text-xs font-medium text-blue-300 hover:underline shrink-0">
                {t('settings.budget.credit.view')}
              </button>
            )}
          </div>
        </Band>
      )}

      {/* Where the money came from — included allowance, promo grants and
          prepaid credit. Overage buckets (extra/grace) are intentionally not
          shown to customers (2026-08-19); the wire still carries them. */}
      <Band title={t('settings.budget.buckets.title')} info={t(personal ? 'settings.budget.buckets.personalDesc' : 'settings.budget.buckets.desc')}>
        {/* All three customer-facing buckets always render, in waterfall order
            (promo → free → prepaid). `free` always shows a value. Promo and
            prepaid show 0.00 when the tenant HAS that source (applicable) and
            '—' when it does not — so a tenant with no promo/credits reads '—'
            rather than a phantom 0.00, while a fresh applicable tenant reads
            0.00. A bucket that funded something always shows its amount (the
            `spend > 0` OR); an older API that omits the flags shows the value.
            Overage buckets (extra/grace) stay hidden; the wire still carries
            them. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatTile
            label={t('settings.budget.bucket.promo')}
            value={bucketApplies(tenant.promoApplicable, tenant.usage.promo)
              ? <MoneyText micro={tenant.usage.promo ?? 0} money={money} />
              : '—'}
            hint={t('settings.budget.bucket.promoHint')}
          />
          <StatTile label={t('settings.budget.bucket.free')} value={<MoneyText micro={tenant.usage.free} money={money} />} hint={t('settings.budget.bucket.freeHint')} />
          <StatTile
            label={t('settings.budget.bucket.prepaid')}
            value={bucketApplies(tenant.prepaidApplicable, tenant.usage.prepaid)
              ? <MoneyText micro={tenant.usage.prepaid ?? 0} money={money} />
              : '—'}
            hint={t('settings.budget.bucket.prepaidHint')}
          />
        </div>
      </Band>

      <Band title={t('settings.budget.feature.title')} info={t('settings.budget.feature.desc')} card>
        <FeatureBreakdown rows={featureRows} money={money} />
      </Band>

      {/* On a personal drive only the feature chart says anything: a pie of
          "spend by member" over one member, or by team over none, is a circle.
          The ORG_BILLING_UX plan reaches the same conclusion for the member
          view — "one person comparing themselves to nothing does not need a
          pie". */}
      {((!personal && (userChart || teamChart)) || featureChart) && (
        <Band title={t('settings.budget.charts.title')}>
          {/* Each chart carries its own caption — two pies and a bar with only
              series legends left the reader to guess which slice of the org
              each one was cutting. */}
          <div className="flex flex-wrap gap-4">
            {!personal && userChart && (
              <div className="flex-1 min-w-[320px] themed-bg-secondary rounded-lg p-3">
                <div className="themed-text text-xs font-medium mb-2">{t('settings.budget.charts.byMemberTitle')}</div>
                <ChartRenderer config={userChart} />
              </div>
            )}
            {!personal && teamChart && (
              <div className="flex-1 min-w-[280px] themed-bg-secondary rounded-lg p-3">
                <div className="themed-text text-xs font-medium mb-2">{t('settings.budget.charts.byTeamTitle')}</div>
                <ChartRenderer config={teamChart} />
              </div>
            )}
            {featureChart && (
              <div className="flex-1 min-w-[280px] themed-bg-secondary rounded-lg p-3">
                <div className="themed-text text-xs font-medium mb-2">{t('settings.budget.charts.byFeatureTitle')}</div>
                <ChartRenderer config={featureChart} />
              </div>
            )}
          </div>
        </Band>
      )}

      {/* Per-member and per-team tables enumerate sets a personal drive has
          exactly one and zero of. Hidden rather than rendered as a table whose
          only row is you. */}
      {!personal && (
      <Band
        title={t('settings.budget.perMember.title')}
        info={t('settings.budget.perMember.desc')}
        card
        right={<span className="themed-text-muted text-[11px] tabular-nums">{t('settings.budget.memberCount')}: {users.length}</span>}
      >
        {/* Filter + paging past the same thresholds `ModelRestrictionsEditor`
            uses, so the two long tables on this surface behave alike. The
            table was unbounded, unsearchable and fixed-sorted: a 200-member
            org got 200 rows on a settings page, and finding one person meant
            scrolling. */}
        {users.length > MEMBER_FILTER_AT && (
          <input
            value={memberQuery}
            onChange={e => { setMemberQuery(e.target.value); setMemberLimit(MEMBER_PAGE) }}
            placeholder={t('settings.budget.perMember.search')}
            aria-label={t('settings.budget.perMember.search')}
            className="themed-input border rounded px-2 py-1 text-xs w-56 mb-2 outline-none focus:border-blue-500"
          />
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.member')}</th>
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.role')}</th>
                <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.spend')}</th>
                <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.cap')}</th>
                {/* M8 — "who is about to be blocked" was mental arithmetic
                    repeated per row, on the one table an admin opens to
                    answer exactly that. */}
                <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.perMember.headroom')}</th>
              </tr>
            </thead>
            <tbody>
              {shownMembers.length === 0 && (
                <tr><td className="px-4 py-3 themed-text-muted text-sm" colSpan={5}>{t('settings.budget.noData')}</td></tr>
              )}
              {shownMembers.map(u => (
                <tr key={u.email} className="border-b themed-border last:border-b-0">
                  <td className="px-4 py-2.5 themed-text-muted font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2.5 themed-text text-xs">
                    {u.role}
                    {/* A platform support seat (0180). The tenant is told
                        plainly: this person is platform staff and their AI
                        usage here is NOT billed to this organization. The
                        field already rode this payload; rendering it is what
                        makes it disclosure rather than something visible only
                        to whoever reads the network response. */}
                    {u.platformSeat && (
                      <span
                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400"
                        title={t('settings.budget.platformSeatHint', {
                          defaultValue: 'Platform support — this person is platform staff. Their AI usage in this organization is billed to the platform, not to you.',
                        })}
                      >
                        {t('settings.budget.platformSeat', { defaultValue: 'Platform support' })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <FigureText figure={{ usd: u.usage.total, displayMinor: u.usage.totalDisplayMinor ?? null }} money={money} className="themed-text" />
                  </td>
                  <td className="px-4 py-2.5 text-right"><CapText cap={u.caps.userCap} money={money} /></td>
                  <td className="px-4 py-2.5 text-right"><Headroom used={u.usage.total} cap={u.caps.userCap} money={money} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(memberQuery || filteredMembers.length > shownMembers.length) && (
          <div className="flex items-center gap-3 mt-2">
            <span className="themed-text-muted text-[11px]">
              {t('settings.budget.perMember.showing', { shown: shownMembers.length, total: users.length })}
            </span>
            {filteredMembers.length > shownMembers.length && (
              <button
                onClick={() => setMemberLimit((n: number) => n + MEMBER_PAGE)}
                className="text-[11px] font-medium text-blue-300 hover:underline"
              >
                {t('settings.budget.perMember.more')}
              </button>
            )}
          </div>
        )}
      </Band>

      )}

      {!personal && (
      <Band title={t('settings.budget.perTeam.title')} info={t('settings.budget.perTeam.desc')} card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b themed-border">
                <th className="text-left px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.team')}</th>
                <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.spend')}</th>
                <th className="text-right px-4 py-2 themed-text-secondary font-medium">{t('settings.budget.col.contribution')}</th>
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 && (
                <tr><td className="px-4 py-3 themed-text-muted text-sm" colSpan={3}>{t('settings.budget.noData')}</td></tr>
              )}
              {teams.map(tm => (
                <tr key={tm.id} className="border-b themed-border last:border-b-0">
                  <td className="px-4 py-2.5 themed-text">{tm.name}</td>
                  <td className="px-4 py-2.5 text-right"><FigureText figure={{ usd: tm.used, displayMinor: tm.usedDisplayMinor ?? null }} money={money} className="themed-text" /></td>
                  {/* Team caps are additive CONTRIBUTIONS — a 0 contributes
                      nothing; it is not a hard block and must not read as one,
                      and it is certainly not "Unlimited". */}
                  <td className="px-4 py-2.5 text-right"><CapText cap={tm.cap} money={money} zeroIsBlock={false} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Band>
      )}

      {/* NOT on a personal drive — `MemberUsageView` makes the same call at its
          own EnforcementNote, and the two tabs must not disagree about it.

          The earlier note here said enforcement is "exactly as relevant to a
          solo user as to an org". The heading survives that argument; the COPY
          does not. `enforcement.desc` reads "How your organization's own caps
          behave when a member runs out", and the `warn_only` panel says role
          caps, team budgets and member adjustments no longer stop a call — a
          personal drive has none of those.

          Worse than merely irrelevant: on a personal drive `warn_only` softens
          NOTHING, because the only cap in play is the platform-set ceiling and
          that hard-stops in either mode. So the amber panel would announce a
          relaxation that does not exist, to the one audience that cannot check
          it against a roles table. */}
      {!personal && (
        <Band title={t('settings.budget.enforcement.title')} card>
          <EnforcementNote mode={data.enforcementMode} />
        </Band>
      )}
    </div>
  )
}
